import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import EventIcon from "@mui/icons-material/Event";
import "./NewEventForm.css";
import EventManager from "../../../utils/managers/EventManager";
import toast from "react-hot-toast";
import { IconButton } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { CLOSE_BTN, ICON_SIZE_SMALL } from "../../../utils/constants";
import formatDate from "../../../utils/formatDate";

function NewEventForm({ eventData, setShowNewEventForm }) {
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    titleUk: eventData?.titles?.uk || "",
    titleEn: eventData?.titles?.en || "",
    descriptionUk: eventData?.descriptions?.uk || "",
    descriptionEn: eventData?.descriptions?.en || "",
    eventLink: eventData?.link || "",
    startDate: eventData ? formatDate(eventData?.dates.start) : "",
    endDate: eventData ? formatDate(eventData?.dates.end) : "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleUpdate = (e) => {
    e.preventDefault();

    const newData = {
      titles: {
        uk: formData.titleUk,
        en: formData.titleEn,
      },
      descriptions: {
        uk: formData.descriptionUk,
        en: formData.descriptionEn,
      },
      link: formData.eventLink,
      dates: {
        start: formData.startDate,
        end: formData.endDate,
      },
    };

    if (
      newData.titles.uk &&
      newData.titles.en &&
      newData.dates.end &&
      newData.dates.start &&
      newData.link
    ) {
      toast
        .promise(EventManager.updateEvent(eventData.id, newData), {
          loading: t("newEventForm.loadingMessage"),
          success: t("newEventForm.updateSuccess"),
          error: t("newEventForm.updateError"),
        })
        .then(() => {
          setFormData({
            titleUk: "",
            titleEn: "",
            descriptionUk: "",
            descriptionEn: "",
            eventLink: "",
            startDate: "",
            endDate: "",
          });
          setShowNewEventForm({ isShown: false });
        });
    } else {
      toast.error(t("newEventForm.errors.validationError"));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      !formData.titleUk ||
      !formData.titleEn ||
      !formData.startDate ||
      !formData.endDate ||
      !formData.eventLink
    )
      return toast.error(t("newEventForm.errors.validationError"));

    const EventDto = {
      titles: {
        uk: formData.titleUk,
        en: formData.titleEn,
      },
      descriptions: {
        uk: formData.descriptionUk,
        en: formData.descriptionEn,
      },
      link: formData.eventLink,
      dates: {
        start: formData.startDate,
        end: formData.endDate,
      },
    };

    toast
      .promise(EventManager.addEvent(EventDto), {
        loading: t("newEventForm.loadingMessage"),
        success: t("newEventForm.successMessage"),
        error: t("newEventForm.errors.unknownError"),
      })
      .then(() => {
        setFormData({
          titleUk: "",
          titleEn: "",
          descriptionUk: "",
          descriptionEn: "",
          eventLink: "",
          startDate: "",
          endDate: "",
        });
        setShowNewEventForm({ isShown: false });
      });
  };

  return (
    <div className="form">
      <div className="form__form-container-wrapper">
        <form
          className="form-container"
          onSubmit={eventData ? handleUpdate : handleSubmit}
        >
          <IconButton
            sx={CLOSE_BTN}
            onClick={() => setShowNewEventForm({ isShown: false })}
            className="close-button"
          >
            <CloseRoundedIcon sx={ICON_SIZE_SMALL} />
          </IconButton>
          <h2 className="form-title">{t("newEventForm.formTitle")}</h2>

          <div>
            <label className="form-label">{t("newEventForm.titleUk")}</label>
            <input
              type="text"
              name="titleUk"
              value={formData.titleUk}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div>
            <label className="form-label">{t("newEventForm.titleEn")}</label>
            <input
              type="text"
              name="titleEn"
              value={formData.titleEn}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div>
            <label className="form-label">{t("newEventForm.descriptionUk")}</label>
            <textarea
              name="descriptionUk"
              value={formData.descriptionUk}
              onChange={handleChange}
              className="form-input form-textarea"
              rows={4}
              placeholder={t("newEventForm.descriptionPlaceholder")}
            />
          </div>

          <div>
            <label className="form-label">{t("newEventForm.descriptionEn")}</label>
            <textarea
              name="descriptionEn"
              value={formData.descriptionEn}
              onChange={handleChange}
              className="form-input form-textarea"
              rows={4}
              placeholder={t("newEventForm.descriptionPlaceholder")}
            />
            <p className="form-hint">{t("newEventForm.descriptionHint")}</p>
          </div>

          <div>
            <label className="form-label">{t("newEventForm.eventLink")}</label>
            <input
              type="text"
              name="eventLink"
              value={formData.eventLink}
              onChange={handleChange}
              className="form-input"
            />
          </div>
          <div className="form__date-container">
            <div>
              <label className="form-label">
                {t("newEventForm.startDate")}
              </label>
              <TextField
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                className="icon-input"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EventIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </div>

            <div>
              <label className="form-label">{t("newEventForm.endDate")}</label>
              <TextField
                type="date"
                name="endDate"
                value={formData.endDate}
                onChange={handleChange}
                className="icon-input"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EventIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </div>

          <button type="submit" className="form-submit">
            {t(eventData ? "newEventForm.updateBtn" : "newEventForm.save")}
          </button>
        </form>
      </div>
    </div>
  );
}

export default NewEventForm;
